import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiSearchLine, RiQrScan2Line } from '@remixicon/react';
import { TrackShell } from './TrackShell';
import BarcodeScanner, { isBarcodeScanSupported } from './BarcodeScanner';

// Public landing for /track (no waybill). Two ways in:
//   1. type a resi → /track/{resi}
//   2. scan a QR/barcode in-page (Chromium only) → parse → /track/{resi}
// The QR-scan-with-native-camera path bypasses this page entirely (the camera
// opens /track/{resi} directly).

// A scanned code may be the full public-track URL or a bare resi. Pull the
// waybill out of a URL if present, else use the raw value.
const extractWaybill = (raw: string): string => {
  const m = raw.match(/\/track\/([^/?#]+)/);
  return (m ? decodeURIComponent(m[1]) : raw).trim();
};

const TrackLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [waybill, setWaybill] = useState('');
  const [scanning, setScanning] = useState(false);

  const go = (value: string) => {
    const resi = value.trim();
    if (resi) navigate(`/track/${encodeURIComponent(resi)}`);
  };

  return (
    <>
      <TrackShell>
        <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-md">
          <h1 className="text-base font-semibold text-text-strong-950">
            Lacak Kiriman Anda
          </h1>
          <p className="mt-1 text-sm text-text-sub-600">
            Masukkan nomor resi, atau pindai QR pada label.
          </p>

          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              go(waybill);
            }}
          >
            <div className="flex items-center gap-2 rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2.5 focus-within:border-primary">
              <RiSearchLine className="size-4 shrink-0 text-text-soft-400" />
              <input
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                value={waybill}
                onChange={(e) => setWaybill(e.target.value)}
                placeholder="Contoh: DASH12345"
                className="w-full bg-transparent text-sm text-text-strong-950 outline-none placeholder:text-text-soft-400"
              />
            </div>

            <button
              type="submit"
              disabled={!waybill.trim()}
              className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-static-white transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              Lacak
            </button>
          </form>

          {isBarcodeScanSupported() && (
            <>
              <div className="my-4 flex items-center gap-3 text-xs text-text-soft-400">
                <span className="h-px flex-1 bg-stroke-soft-200" />
                atau
                <span className="h-px flex-1 bg-stroke-soft-200" />
              </div>
              <button
                type="button"
                onClick={() => setScanning(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-stroke-sub-300 px-4 py-2.5 text-sm font-medium text-text-strong-950 transition-colors hover:bg-bg-weak-50"
              >
                <RiQrScan2Line className="size-4 text-primary" />
                Pindai QR / Barcode
              </button>
            </>
          )}
        </div>
      </TrackShell>

      {scanning && (
        <BarcodeScanner
          onDetected={(raw) => {
            setScanning(false);
            go(extractWaybill(raw));
          }}
          onClose={() => setScanning(false)}
        />
      )}
    </>
  );
};

export default TrackLandingPage;
